import Typo from "@/src/components/atom/Typo";
import PlanSelectionGrid, {
  PaymentCycle,
} from "@/src/components/organism/PlanSelectionGrid";
import ExporterMainLayout from "@/src/components/template/Layout/exporter/ExporterMainLayout";
import EXPORTER_PRIVATE_PATH from "@/src/routes/exporter/path";
import colorSet from "@/src/styles/color";
import {
  useGetPlansQuery,
  useGetCurrentSubscriptionQuery,
  useGetPaymentMethodsQuery,
} from "@/src/store/apis/subscription";
import type {
  PlanDto,
  PlanVersionDto,
} from "@/src/store/apis/subscription/interface";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { notification } from "antd";
import SubscriptionPlanModal from "../SubscriptionPlan/dialog/SubscriptionPlanModal";

function PlanChangePage() {
  const { t } = useTranslation([
    "subscriptionPlanSignup",
    "subscriptionManagement",
    "common",
  ]);
  const navigate = useNavigate();

  const [paymentCycle, setPaymentCycle] = useState<PaymentCycle>("MONTHLY");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<PlanDto | null>(null);
  const [selectedPlanVersion, setSelectedPlanVersion] =
    useState<PlanVersionDto | null>(null);

  const { data: currentSubscription, isLoading: isLoadingCurrentSubscription } =
    useGetCurrentSubscriptionQuery();

  const { data: plansData, isLoading: isLoadingPlans } = useGetPlansQuery({
    planType: "NORMAL",
  });

  const { data: paymentMethodsData } = useGetPaymentMethodsQuery({
    page: 1,
    pageSize: 1,
  });

  const existingPaymentMethod = paymentMethodsData?.rows?.[0];

  const isLoading = isLoadingCurrentSubscription || isLoadingPlans;

  const plans = plansData?.rows || [];

  const handleSelectPlan = useCallback(
    (plan: PlanDto, planVersion: PlanVersionDto) => {
      if (!currentSubscription) return;

      if (plan.id === currentSubscription.plan?.id) {
        notification.info({
          message: t("subscriptionManagement:alreadySubscribedPlan"),
        });
        return;
      }

      setSelectedPlan(plan);
      setSelectedPlanVersion(planVersion);
      setIsModalOpen(true);
    },
    [currentSubscription, t],
  );

  const handleContactSales = useCallback(() => {
    navigate(`${EXPORTER_PRIVATE_PATH.ENTERPRISE_SIGNUP}?isInquiry=true`);
  }, [navigate]);

  const handlePaymentCycleChange = useCallback((cycle: PaymentCycle) => {
    setPaymentCycle(cycle);
  }, []);

  return (
    <ExporterMainLayout
      breadcrumb={[
        t("sideNav:workspaceSettings"),
        t("sideNav:subscription"),
        t("subscriptionManagement:subscription.button.planChange"),
      ]}
      pageTitle={t("subscriptionManagement:subscription.button.planChange")}
    >
      <Container>
        <ContentCard>
          <SectionHeader>
            <SectionTitle>
              {t("subscriptionPlanSignup:planInformation")}
            </SectionTitle>
          </SectionHeader>

          <ContentBody>
            <DescriptionSection>
              <MainTitle>
                {t("subscriptionPlanSignup:selectSubscriptionPlan")}
              </MainTitle>
              <Description>
                {t("subscriptionPlanSignup:planDescription")}
              </Description>
            </DescriptionSection>

            <PlanSelectionGrid
              plans={plans}
              paymentCycle={paymentCycle}
              onPaymentCycleChange={handlePaymentCycleChange}
              onPlanSelect={handleSelectPlan}
              onEnterpriseContact={handleContactSales}
              isLoading={isLoading}
              currentSubscription={currentSubscription}
            />
          </ContentBody>
        </ContentCard>
      </Container>

      {selectedPlan && selectedPlanVersion && (
        <SubscriptionPlanModal
          open={isModalOpen}
          onOpenChange={setIsModalOpen}
          plan={selectedPlan}
          planVersion={selectedPlanVersion}
          paymentCycle={paymentCycle}
          paymentMethod={existingPaymentMethod}
          isChangePlan={true}
          currentSubscriptionId={currentSubscription?.id}
        />
      )}
    </ExporterMainLayout>
  );
}

export default PlanChangePage;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  background: white;
`;

const ContentCard = styled.div`
  box-shadow: 0px 4px 15px rgba(0, 0, 0, 0.05);
  border-radius: 16px;
  padding: 8px;
`;

const SectionHeader = styled.div`
  padding: 12px 16px;
  background: ${colorSet.gray11};
  border-radius: 8px 8px 0 0;
`;

const SectionTitle = styled(Typo)`
  color: ${colorSet.gray2};
  font-size: 17px;
  font-weight: 700;
  line-height: 24px;
`;

const ContentBody = styled.div`
  padding: 24px 16px 16px;
  display: flex;
  flex-direction: column;
  gap: 40px;
  background: white;
  border-radius: 0 0 8px 8px;
`;

const DescriptionSection = styled.div`
  max-width: 544px;
  margin: 0 auto;
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const MainTitle = styled(Typo)`
  color: ${colorSet.gray2};
  font-size: 26px;
  font-weight: 700;
  line-height: 38px;
`;

const Description = styled(Typo)`
  color: ${colorSet.gray5};
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  white-space: pre-line;
`;
